import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useData } from "@/contexts/DataContext";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import {
  ChevronLeft,
  User as UserIcon,
  Building2,
  MapPin,
  Phone,
  Mail,
  Globe,
  BadgeCheck,
  Loader2,
  Save,
  ShieldCheck,
  Lock,
  Upload,
  Trash2
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { motion, AnimatePresence } from "framer-motion";
import type { BusinessProfile } from "@/lib/firebase";

export default function ProfilePage() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const {
    businessProfile,
    saveBusinessProfile,
    userMode,
    businessLogo,
    updateBusinessLogo,
    isUltimateOwnerMobile,
  } = useData();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [showLockedDialog, setShowLockedDialog] = useState(false);
  const [showRemoveLogoConfirm, setShowRemoveLogoConfirm] = useState(false);

  const [profileForm, setProfileForm] = useState<BusinessProfile>({
    ownerName: "",
    businessName: "",
    businessAddress: "",
    businessEmail: "",
    businessPhone: "",
    businessWebsite: "",
    isGstEnabled: false,
    gstInclusive: false,
    gstin: "",
    cin: "",
    fssai: "",
  });

  // 🛡️ Lock Logic Sync: Only locked if they actually have a truthy value
  const isOwnerLocked = !!businessProfile?.ownerName;
  const isAddressLocked = !!businessProfile?.businessAddress;
  const isWebsiteLocked = !!businessProfile?.businessWebsite;

  useEffect(() => {
    if (businessProfile) {
      setProfileForm(businessProfile);
    }
  }, [businessProfile]);

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      setLocation("/settings");
    }
  };

  const handleLockedFieldClick = () => {
    setShowLockedDialog(true);
  };

  const getInitials = (name?: string | null) => {
    if (!name) return "B";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  // 🚨 Logo Upload & Compression Logic
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type)) {
      toast({ title: "Invalid file type", description: "Please upload a .jpg, .jpeg or .png image", variant: "destructive" });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast({ title: "File too large", description: "Logo size must be less than 2MB", variant: "destructive" });
      return;
    }

    setIsUploadingLogo(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const img = new Image();
        img.onload = async () => {
          const canvas = document.createElement("canvas");
          const size = Math.min(img.width, img.height, 400);
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext("2d");
          if (!ctx) throw new Error("Canvas context failed");

          const minDim = Math.min(img.width, img.height);
          const sx = (img.width - minDim) / 2;
          const sy = (img.height - minDim) / 2;
          ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, size, size);

          const compressedBase64 = canvas.toDataURL("image/jpeg", 0.7);

          if (compressedBase64.length > 1000000) {
            toast({ title: "Image too complex", description: "Please use a simpler image for your logo", variant: "destructive" });
            setIsUploadingLogo(false);
            return;
          }

          await updateBusinessLogo(compressedBase64);
          toast({ title: "Business logo updated" });
          setIsUploadingLogo(false);
        };
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    } catch (error: any) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
      setIsUploadingLogo(false);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    // 🛡️ Pro-Only Validation
    if (userMode === "pro" && profileForm.isGstEnabled) {
      if (profileForm.gstin && !/^[A-Z0-9]{15}$/.test(profileForm.gstin)) {
        toast({ title: "Invalid GSTIN", description: "Must be 15 alphanumeric characters.", variant: "destructive" });
        return;
      }
      if (profileForm.cin && !/^[A-Z0-9]{21}$/.test(profileForm.cin)) {
        toast({ title: "Invalid CIN", description: "Must be 21 alphanumeric characters.", variant: "destructive" });
        return;
      }
      if (profileForm.fssai && !/^\d{14}$/.test(profileForm.fssai)) {
        toast({ title: "Invalid FSSAI", description: "Must be 14 digits.", variant: "destructive" });
        return;
      }
    }

    setIsSaving(true);
    try {
      const cleanWebsite = profileForm.businessWebsite
        ? profileForm.businessWebsite.replace(/^(https?:\/\/)?(www\.)?/, "").replace(/\/$/, "")
        : "";

      const updatedProfile = {
        ...profileForm,
        businessWebsite: cleanWebsite,
        businessEmail: businessProfile?.businessEmail || user?.email || profileForm.businessEmail,
        phoneVerified: user?.phoneNumber ? profileForm.businessPhone === user.phoneNumber : false,
      };

      await saveBusinessProfile(updatedProfile);
      toast({ title: "Profile updated successfully" });
      handleBack();
    } catch (error: any) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614]">
      {/* Header */}
      {isUltimateOwnerMobile ? (
        <div className="px-4 pb-2" style={{ paddingTop: "calc(env(safe-area-inset-top) + 1.25rem)" }}>
          <div className="flex items-start gap-3">
            <button onClick={handleBack} className="rounded-full p-2 border border-slate-200 dark:border-[#454545] bg-white dark:bg-[#1F1B19]/60 mt-1 shrink-0">
              <ChevronLeft className="h-4 w-4 text-[#0F172A] dark:text-[#EEEAE3]" />
            </button>
            <div className="min-w-0">
              <h1 className="text-2xl font-black uppercase tracking-tighter leading-[0.95] text-[#0F172A] dark:text-[#EEEAE3]">
                BUSINESS<br /><span className="text-[#8B1A1A]">PROFILE</span>
              </h1>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-[#8A8A92] mt-2">
                Name · Address · GSTIN
              </p>
            </div>
          </div>
        </div>
      ) : (
        <header className="px-4 py-4 flex items-center sticky top-0 z-30 bg-[#FFFBF2]/90 dark:bg-[#1A1614]/90 backdrop-blur-md border-b border-slate-200/50">
          <button
            onClick={handleBack}
            className="w-10 h-10 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center shadow-sm border border-slate-100 dark:border-slate-700 active:scale-95 transition-transform shrink-0"
          >
            <ChevronLeft className="w-6 h-6 text-slate-800 dark:text-white" />
          </button>
          <h1 className="ml-4 text-xl font-black text-slate-900 dark:text-white tracking-tight">Business Profile</h1>
        </header>
      )}

      <div className="flex-1 px-4 py-6 pb-24 overflow-y-auto">
        <form onSubmit={handleProfileUpdate} className="space-y-6 max-w-md mx-auto">

          {/* Business Logo Section */}
          <div className="flex flex-col items-center justify-center pt-2 pb-2">
            <div className="relative group cursor-pointer" onClick={() => setLogoDialogOpen(true)}>
              <Avatar className="h-28 w-28 border-[4px] border-white dark:border-slate-800 shadow-xl bg-slate-50 dark:bg-slate-900">
                <AvatarImage src={businessLogo || undefined} className="object-cover" />
                <AvatarFallback className="text-3xl text-[#8B1A1A] font-black">
                  {getInitials(profileForm.businessName)}
                </AvatarFallback>
              </Avatar>
              <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Upload className="h-8 w-8 text-white" />
              </div>
              <div className="absolute bottom-0 right-0 bg-[#8B1A1A] p-2 rounded-full border-[3px] border-white dark:border-slate-900 shadow-sm transition-transform active:scale-95">
                <Upload className="h-4 w-4 text-white" />
              </div>
            </div>
            <p
              className="text-[10px] font-black text-slate-400 mt-4 uppercase tracking-widest cursor-pointer hover:text-[#8B1A1A] transition-colors"
              onClick={() => setLogoDialogOpen(true)}
            >
              {businessLogo ? "Change Logo" : "Upload Logo"}
            </p>
          </div>

          {/* Section 1: Core Identity */}
          <div className="bg-white dark:bg-slate-900 rounded-[32px] p-6 border border-slate-100 dark:border-slate-800 shadow-sm space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-[#8B1A1A]" />
              <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Business Identity</h2>
            </div>

            <div className="space-y-1.5" onClick={isOwnerLocked ? handleLockedFieldClick : undefined}>
              <Label className={`text-xs font-bold text-slate-500 uppercase ml-1 flex items-center gap-1 ${isOwnerLocked ? 'cursor-pointer' : ''}`}>
                Owner Name {isOwnerLocked && <Lock className="w-2.5 h-2.5 text-slate-400" />}
              </Label>
              <Input
                className={`h-12 rounded-2xl bg-slate-50 dark:bg-slate-950 border-none disabled:opacity-60 ${isOwnerLocked ? 'cursor-pointer pointer-events-none' : ''}`}
                value={profileForm.ownerName}
                onChange={(e) => setProfileForm({ ...profileForm, ownerName: e.target.value })}
                disabled={isOwnerLocked}
                placeholder="Enter owner name"
              />
            </div>

            <div className="space-y-1.5" onClick={handleLockedFieldClick}>
              <Label className="text-xs font-bold text-slate-500 uppercase ml-1 flex items-center gap-1 cursor-pointer">
                Business Name <Lock className="w-2.5 h-2.5 text-slate-400" />
              </Label>
              <Input
                className="h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 border-none opacity-70 cursor-pointer pointer-events-none"
                value={profileForm.businessName}
                disabled={true}
              />
            </div>

            <div className="space-y-1.5" onClick={isAddressLocked ? handleLockedFieldClick : undefined}>
              <Label className={`text-xs font-bold text-slate-500 uppercase ml-1 flex items-center gap-1 ${isAddressLocked ? 'cursor-pointer' : ''}`}>
                Business Address {isAddressLocked && <Lock className="w-2.5 h-2.5 text-slate-400" />}
              </Label>
              <textarea
                className={`w-full min-h-[100px] p-4 rounded-2xl bg-slate-50 dark:bg-slate-950 border-none text-sm focus:ring-2 focus:ring-[#8B1A1A]/20 outline-none transition-all disabled:opacity-60 ${isAddressLocked ? 'cursor-pointer pointer-events-none' : ''}`}
                value={profileForm.businessAddress}
                onChange={(e) => setProfileForm({ ...profileForm, businessAddress: e.target.value })}
                disabled={isAddressLocked}
                placeholder="Enter business address..."
              />
            </div>
          </div>

          {/* Section 2: Contact Info */}
          <div className="bg-white dark:bg-slate-900 rounded-[32px] p-6 border border-slate-100 dark:border-slate-800 shadow-sm space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Phone className="w-4 h-4 text-[#8B1A1A]" />
              <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Contact Details</h2>
            </div>

            <div className="space-y-1.5" onClick={handleLockedFieldClick}>
              <Label className="text-xs font-bold text-slate-500 uppercase ml-1 flex items-center gap-1 cursor-pointer">Mobile Number <Lock className="w-2.5 h-2.5 text-slate-400" /></Label>
              <div className="flex items-center gap-2 pointer-events-none">
                <div className="flex items-center gap-1.5 px-4 bg-slate-100 dark:bg-slate-800 rounded-2xl h-12 shrink-0 select-none opacity-70">
                  <span className="text-base leading-none">🇮🇳</span>
                  <span className="font-bold text-slate-500">+91</span>
                </div>
                <div className="relative flex-1">
                  <Input
                    className="h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 border-none opacity-70 w-full pr-12 cursor-pointer"
                    value={profileForm.businessPhone.startsWith("+91") ? profileForm.businessPhone.slice(3).trim() : profileForm.businessPhone.replace(/\D/g, "").slice(-10)}
                    disabled={true}
                  />
                  <BadgeCheck className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-emerald-500" />
                </div>
              </div>
            </div>

            <div className="space-y-1.5" onClick={isWebsiteLocked ? handleLockedFieldClick : undefined}>
              <Label className={`text-xs font-bold text-slate-500 uppercase ml-1 flex items-center gap-1 ${isWebsiteLocked ? 'cursor-pointer' : ''}`}>
                Website {isWebsiteLocked && <Lock className="w-2.5 h-2.5 text-slate-400" />}
              </Label>
              <div className={`relative ${isWebsiteLocked ? 'pointer-events-none' : ''}`}>
                <Globe className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  className={`h-12 rounded-2xl pl-10 bg-slate-50 dark:bg-slate-950 border-none disabled:opacity-60 ${isWebsiteLocked ? 'cursor-pointer' : ''}`}
                  value={profileForm.businessWebsite}
                  onChange={(e) => setProfileForm({ ...profileForm, businessWebsite: e.target.value })}
                  placeholder="www.yourbusiness.com"
                  disabled={isWebsiteLocked}
                />
              </div>
            </div>
          </div>

          {/* Section 3: Pro Registration */}
          {userMode === "pro" && (
            <div className="bg-white dark:bg-slate-900 rounded-[32px] p-6 border border-slate-100 dark:border-slate-800 shadow-sm">
              <div className="flex items-center justify-between py-2 mb-4">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-[#8B1A1A]" />
                  <div>
                    <h2 className="text-sm font-bold text-slate-900 dark:text-white leading-none">GST Billing</h2>
                    <p className="text-[10px] text-slate-400 font-medium mt-1">Enable for Tax Invoices</p>
                  </div>
                </div>
                <Switch
                  checked={profileForm.isGstEnabled}
                  onCheckedChange={(checked) => setProfileForm({ ...profileForm, isGstEnabled: checked })}
                />
              </div>

              <AnimatePresence>
                {profileForm.isGstEnabled && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-4 overflow-hidden"
                  >
                    <div className="space-y-1.5 pt-2 border-t border-slate-50 dark:border-slate-800 mt-2">
                      <Label className="text-[10px] font-black text-slate-400 uppercase">GSTIN Number</Label>
                      <Input
                        className="h-12 rounded-2xl bg-slate-50 dark:bg-slate-950 border-none uppercase"
                        value={profileForm.gstin}
                        onChange={(e) => setProfileForm({ ...profileForm, gstin: e.target.value.toUpperCase() })}
                        placeholder="22AAAAA0000A1Z5"
                        maxLength={15}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-[10px] font-black text-slate-400 uppercase">CIN Number</Label>
                      <Input
                        className="h-12 rounded-2xl bg-slate-50 dark:bg-slate-950 border-none uppercase"
                        value={profileForm.cin}
                        onChange={(e) => setProfileForm({ ...profileForm, cin: e.target.value.toUpperCase() })}
                        placeholder="L12345MH2019PTC123456"
                        maxLength={21}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-[10px] font-black text-slate-400 uppercase">FSSAI Licence No</Label>
                      <Input
                        className="h-12 rounded-2xl bg-slate-50 dark:bg-slate-950 border-none"
                        value={profileForm.fssai}
                        onChange={(e) => setProfileForm({ ...profileForm, fssai: e.target.value.replace(/\D/g, "") })}
                        placeholder="14 Digit Number"
                        maxLength={14}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          <div className="mt-8">
            <Button
              type="submit"
              disabled={isSaving}
              className="w-full h-14 rounded-[2rem] bg-[#8B2121] hover:bg-[#6B1919] text-white font-bold text-lg shadow-lg shadow-red-900/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            >
              {isSaving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </div>

        </form>
      </div>

      {/* 🛡️ Full Screen Field Locked Overlay */}
      <AlertDialog open={showLockedDialog} onOpenChange={setShowLockedDialog}>
        <AlertDialogContent className="max-w-full w-full h-[100dvh] p-0 m-0 rounded-none border-none bg-[#FFFBF2] dark:bg-[#1A1614] flex flex-col shadow-none overflow-hidden z-[500]">
          <div className="px-4 pt-[calc(env(safe-area-inset-top)+1rem)] pb-4 flex items-center shrink-0">
            <button
              onClick={() => setShowLockedDialog(false)}
              className="w-10 h-10 flex items-center justify-center bg-white dark:bg-slate-800 rounded-full shadow-sm border border-slate-100 dark:border-slate-700 active:scale-95"
            >
              <ChevronLeft className="w-6 h-6 text-slate-800 dark:text-white" />
            </button>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
            <div className="w-20 h-20 bg-red-50 dark:bg-red-900/30 rounded-[32px] flex items-center justify-center mb-6 border border-red-100 dark:border-red-800 rotate-12">
              <Lock className="w-10 h-10 text-[#8B1A1A] dark:text-red-400 -rotate-12" />
            </div>
            <AlertDialogTitle className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-4">
              Locked
            </AlertDialogTitle>
            <AlertDialogDescription className="text-lg font-medium text-slate-500 dark:text-slate-400 leading-relaxed max-w-xs">
              To protect your business identity, this information cannot be changed directly.
              <br /><br />
              Please contact our support team to request an update.
            </AlertDialogDescription>
          </div>
          <div className="p-4 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] flex flex-col gap-3 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800">
            <Button
              className="w-full h-16 rounded-[20px] bg-[#8B1A1A] hover:bg-[#631212] text-white font-black text-lg uppercase tracking-widest shadow-xl shadow-red-900/20 active:scale-95 transition-all"
              onClick={() => { setShowLockedDialog(false); setLocation("/support"); }}
            >
              Contact Support
            </Button>
            <AlertDialogCancel
              className="w-full h-14 rounded-[20px] font-bold text-slate-500 dark:text-slate-400 border-none bg-transparent active:scale-95 transition-all mt-0"
              onClick={() => setShowLockedDialog(false)}
            >
              Cancel
            </AlertDialogCancel>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* 🚀 Full Screen Logo Management Overlay */}
      <Dialog open={logoDialogOpen} onOpenChange={setLogoDialogOpen}>
        <DialogContent className="max-w-full w-full h-[100dvh] p-0 m-0 rounded-none border-none bg-[#FFFBF2] dark:bg-[#1A1614] flex flex-col shadow-none overflow-hidden z-[400]">
          <div className="sr-only"><DialogTitle>Business Logo Management</DialogTitle></div>
          <div className="px-4 pt-[calc(env(safe-area-inset-top)+1rem)] pb-4 flex items-center shrink-0">
            <button
              onClick={() => setLogoDialogOpen(false)}
              className="w-10 h-10 flex items-center justify-center bg-white dark:bg-slate-800 rounded-full shadow-sm border border-slate-100 dark:border-slate-700 active:scale-95"
            >
              <ChevronLeft className="w-6 h-6 text-slate-800 dark:text-white" />
            </button>
            <h2 className="ml-4 text-xl font-black text-slate-900 dark:text-white">Business Logo</h2>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
            <div className="relative mb-12">
              <Avatar className="h-44 w-44 border-8 border-white dark:border-slate-800 shadow-2xl">
                <AvatarImage src={businessLogo || undefined} className="object-cover" />
                <AvatarFallback className="text-5xl font-black bg-slate-100 dark:bg-slate-800 text-[#8B1A1A] dark:text-white">
                  {getInitials(profileForm.businessName)}
                </AvatarFallback>
              </Avatar>
              <div className="absolute -bottom-2 -right-2 bg-[#8B1A1A] p-4 rounded-[24px] shadow-xl border-4 border-[#FFFBF2] dark:border-[#1A1614]">
                <Upload className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="w-full bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-[32px] p-6 text-amber-800 dark:text-amber-300 shadow-sm max-w-sm">
              <p className="font-black uppercase tracking-widest text-[10px] mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Thermal Printing Tips
              </p>
              <ul className="space-y-1.5 font-medium text-xs opacity-90 list-none text-left">
                <li>• Format: PNG or JPEG (best for printers)</li>
                <li>• Recommended size: 200 × 200 px</li>
                <li>• Black & white prints clearest on thermal</li>
                <li>• Keep it simple — no fine details</li>
              </ul>
            </div>
          </div>
          <input type="file" ref={fileInputRef} onChange={handleLogoUpload} className="hidden" accept="image/*" />
          <div className="p-4 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] flex flex-col gap-3 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800">
            <Button
              disabled={isUploadingLogo}
              onClick={() => fileInputRef.current?.click()}
              className="w-full h-16 rounded-[20px] bg-[#8B1A1A] hover:bg-[#631212] text-white font-black text-lg uppercase tracking-widest shadow-xl shadow-red-900/20 active:scale-95 transition-all"
            >
              {isUploadingLogo ? <Loader2 className="w-5 h-5 animate-spin" /> : businessLogo ? "Change Logo" : "Upload Logo"}
            </Button>
            {businessLogo && (
              <button
                onClick={() => setShowRemoveLogoConfirm(true)}
                className="w-full h-14 rounded-[20px] font-black uppercase tracking-widest text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 transition-all active:scale-95"
              >
                Remove Current Logo
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* 🚀 Full Screen Remove Logo Confirmation Overlay */}
      <Dialog open={showRemoveLogoConfirm} onOpenChange={setShowRemoveLogoConfirm}>
        <DialogContent className="max-w-full w-full h-[100dvh] p-0 m-0 rounded-none border-none bg-[#FFFBF2] dark:bg-[#1A1614] flex flex-col shadow-none overflow-hidden z-[600]">
          <div className="sr-only"><DialogTitle>Remove Logo Confirmation</DialogTitle></div>
          <div className="px-4 pt-[calc(env(safe-area-inset-top)+1rem)] pb-4 flex items-center shrink-0">
            <button
              onClick={() => setShowRemoveLogoConfirm(false)}
              className="w-10 h-10 flex items-center justify-center bg-white dark:bg-slate-800 rounded-full shadow-sm border border-slate-100 dark:border-slate-700 active:scale-95"
            >
              <ChevronLeft className="w-6 h-6 text-slate-800 dark:text-white" />
            </button>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
            <div className="w-24 h-24 bg-red-50 dark:bg-red-900/30 rounded-[40px] flex items-center justify-center mb-8 border-4 border-red-100 dark:border-red-800 shadow-xl rotate-12">
              <Trash2 className="w-12 h-12 text-[#8B1A1A] dark:text-red-400 -rotate-12" />
            </div>
            <h3 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight mb-4">
              Remove Logo?
            </h3>
            <p className="text-xl font-medium text-slate-500 dark:text-slate-400 leading-relaxed max-w-xs">
              This will remove your logo from all future invoices. This action cannot be undone.
            </p>
          </div>
          <div className="p-4 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] flex flex-col gap-3 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800">
            <Button
              className="w-full h-16 rounded-[20px] bg-red-600 hover:bg-red-700 text-white font-black text-lg uppercase tracking-widest shadow-xl shadow-red-900/20 active:scale-95 transition-all"
              onClick={async () => {
                setShowRemoveLogoConfirm(false);
                await updateBusinessLogo(null);
                toast({ title: "Logo removed" });
                setLogoDialogOpen(false);
              }}
            >
              Yes, Remove It
            </Button>
            <button
              className="w-full h-14 rounded-[20px] font-bold text-slate-500 dark:text-slate-400 bg-transparent active:scale-95 transition-all"
              onClick={() => setShowRemoveLogoConfirm(false)}
            >
              Cancel
            </button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}